import { Badge, Button, DisplayText } from '@superops/ui-design-system';
import {
  ALL_MODULES,
  DEFAULT_FIELDS_BY_COLUMN_NAME,
  useIsAuthorisedHook,
  withErrorBoundary,
  useUserMSPCheckHook,
  TICKET_SOURCE,
  getCurrentUserHook,
  EventEmitter,
  getConversationOrder,
  useActivityTracker,
  EventEmitterWithIndex
} from '@nile/components';
import React, { useEffect, useState } from 'react';
import { SVGIcon } from '@superops/super-icons';
import { FormattedMessage } from 'react-intl';
import {
  AccountClient,
  GET_FIELD,
  GET_STATUS_CONFIGURATION,
  SAVE_TICKET_CONVERSATION,
  SAVE_TICKET_INTEGRATION_CONVERSATION
} from '@nile/graphql';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Tooltip } from 'react-clone-tippy';
import { useSuperEditor } from '@nile/components/src/Components/RichTextEditor/useSuperEditor';
import { useTicketConversationActionStore } from '@nile/mangifera/src/StoreManager/store/TicketDetails/TicketConversationActionStore';
import { isEmpty } from 'lodash';
import { AI_AGENTS_CONSTANT_FEATURE_ID_MAP } from '@nile/components/src/Components/AI/helpers/constants';

const LABEL_KEYS = {
  CLOSE: 'ticket.details.conversation.sendAndClose',
  RESOLVE: 'ticket.details.conversation.sendAndResolve'
};

const ICON = {
  CLOSE: 'Power',
  RESOLVE: 'Resolution'
};

const SendTicketConversationAction = (props) => {
  const { data } = props;

  const conversationOrder = getConversationOrder() || 'ASC';

  const editorConfig = data?.config?.editor?.storeConfig;
  const ticket = data?.data?.ticket;
  const ticketId = ticket?.id;
  const requester = ticket?.requester;
  const currentUser = getCurrentUserHook();
  const updateTabProperty = props?.updateTabProperty;
  const imConfig = data?.data?.imConfig;

  const { template, resetTemplate } = useSuperEditor(editorConfig);

  const ticketConversationStore = useTicketConversationActionStore({
    ticketId: ticketId
  });

  const conversationTo = ticketConversationStore(
    (state) => state.conversation.to
  );
  const cc = ticketConversationStore((state) => state.conversation.cc);
  const bcc = ticketConversationStore((state) => state.conversation.bcc);
  const selectedMailbox = ticketConversationStore(
    (state) => state.conversation.selectedMailbox
  );
  const conversationScope = ticketConversationStore(
    (state) => state.conversation.conversationScope
  );
  const resetConversationData = ticketConversationStore(
    (state) => state.resetConversationData
  );

  const isTriageConversation = ticketConversationStore(
    (state) => state.isTriageConversation
  );
  const setIsTriageConversation = ticketConversationStore(
    (state) => state.setIsTriageConversation
  );

  const resetData = () => {
    resetConversationData();
    resetTemplate();
  };

  const isPrivateChannel = conversationScope === 'PRIVATE';

  const [loading, setIsLoading] = useState(false);

  const [splitOptions, setSplitOptions] = useState(null);

  const isMSP = useUserMSPCheckHook();

  const isStatusAuthorised = useIsAuthorisedHook('customFields.view');

  const [getStatus, { loading: statusLoading }] = useLazyQuery(GET_FIELD, {
    client: AccountClient,
    variables: {
      input: {
        module: ALL_MODULES.ticket,
        columnName: DEFAULT_FIELDS_BY_COLUMN_NAME.STATUS
      }
    }
  });

  const [
    getStatusConfig,
    { loading: ticketStatusConfigLoading }
  ] = useLazyQuery(GET_STATUS_CONFIGURATION, {
    fetchPolicy: 'cache-and-network'
  });

  const saveConversationQuery =
    ticket?.source === TICKET_SOURCE.Communication
      ? SAVE_TICKET_INTEGRATION_CONVERSATION
      : SAVE_TICKET_CONVERSATION;

  const [
    saveTicketConversation,
    { loading: saveConversationLoading, error: newConversationError }
  ] = useMutation(saveConversationQuery, {
    update(cache, { data }) {
      const conversation = data?.saveTicketConversation;
      if (!conversation) return;
      const newFeed = {
        __typename: 'TicketAudit',
        id: conversation.conversationId,
        type: 'CONVERSATION',
        time: conversation.time,

        user: {
          userId: currentUser?.userId,
          name: currentUser?.name,
          firstName: currentUser?.firstName,
          lastName: currentUser?.lastName,
          email: currentUser?.email
        },

        conversation: {
          __typename: 'Conversation',
          content: conversation.content,
          time: conversation.time,
          user: conversation.user,
          type: conversation.type,
          conversationId: conversation.conversationId,
          outgoingMailStatus: conversation.outgoingMailStatus,
          outgoingMailFailureReason: conversation.outgoingMailFailureReason,
          attachments: conversation.attachments ?? [],
          toUsers: conversation.toUsers ?? null,
          ccUsers: conversation.ccUsers ?? null,
          bccUsers: conversation.bccUsers ?? null
        },

        threadConversation: null,
        conversationThread: null,
        vendorConversation: null,
        note: null,
        approval: null,
        task: null
      };

      cache.modify({
        fields: {
          getTicketAuditList(existing = {}, { readField }) {
            const existingFeeds = existing.ticketAudit ?? [];
            // prevent duplicate insert (retries / optimistic)
            const alreadyExists = existingFeeds.some(
              (ref) => readField('id', ref) === newFeed.id
            );
            if (alreadyExists) return existing;

            return {
              ...existing,
              ticketAudit:
                conversationOrder === 'DESC'
                  ? [newFeed, ...existingFeeds]
                  : [...existingFeeds, newFeed]
            };
          }
        }
      });
    },

    onCompleted() {
      useActivityTracker({ event: 'Ticket - Sending replies on tickets' });
      EventEmitter.dispatch('focusLastAddedMessage');
      resetData();
      EventEmitter.dispatch('closeGlobalConversation', props?.data?.id);
    }
  });

  const getSplitOptions = async () => {
    if (isMSP && isStatusAuthorised) {
      const statusConfigData = await getStatusConfig();
      const statusDataData = await getStatus();

      const configData = statusConfigData?.data?.getStatusConfiguration;
      const statusOptions = statusDataData?.data?.getField?.options;

      const resolveConfig = configData.find(
        (config) => config.configuration === 'RESOLVE'
      );
      const closeConfig = configData.find(
        (config) => config.configuration === 'CLOSE'
      );

      if (statusOptions?.length) {
        // Initialize arrays to hold different status types
        let resolvedStatus = null;
        let closedStatus = null;
        const otherStatuses = [];

        // Categorize each status
        statusOptions.forEach((status) => {
          const value = status?.value;
          const isResolve = value === resolveConfig?.status?.name;
          const isClose = value === closeConfig?.status?.name;

          // Sort into appropriate category
          if (isResolve) {
            resolvedStatus = {
              value: value,
              type: 'RESOLVE'
            };
          } else if (isClose) {
            closedStatus = {
              value: value,
              type: 'CLOSE'
            };
          } else {
            otherStatuses.push({
              value: value,
              type: null
            });
          }
        });

        // Combine arrays in desired order: other statuses, then resolved, then closed
        const splitOptionsData = [
          ...otherStatuses,
          ...(resolvedStatus ? [resolvedStatus] : []),
          ...(closedStatus ? [closedStatus] : [])
        ];

        // Update state with ordered statuses
        setSplitOptions(splitOptionsData);
      }
    }
  };

  const formatUsers = (users) => {
    if (!users || !users?.length) {
      return [];
    }
    return users?.map((user) => {
      return {
        email: user?.email || user?.value
      };
    });
  };

  const formatAttachments = (attachments) => {
    if (!attachments || !attachments?.length) {
      return [];
    }
    return attachments?.map((attachment) => {
      return {
        fileName: attachment?.fileName,
        originalFileName: attachment?.originalFileName,
        fileSize: attachment?.fileSize
      };
    });
  };

  const getRecipientDetails = () => {
    const senderData = {
      user: {
        userId: currentUser?.userId
      },
      referenceId: null,
      label: currentUser?.email,
      name: currentUser?.name,
      recipientType: 'SENDER'
    };
    if (imConfig?.supportedChannelType === 'DM') {
      const recipientDetails = [
        {
          user: {
            userId: ticket?.requester?.userId
          },
          referenceId: null,
          label: ticket?.requester?.email,
          name: ticket?.requester?.name,
          recipientType:
            ticket?.requester?.email === currentUser?.email
              ? 'SENDER'
              : 'RECEIVER'
        }
      ];

      const isSenderPresent = recipientDetails?.find(
        (recipient) => recipient?.user?.userId === currentUser?.userId
      );
      if (!isSenderPresent) {
        recipientDetails?.push(senderData);
      }
      return recipientDetails;
    }

    if (isPrivateChannel) {
      const recipientData = conversationTo?.map((recipient) => {
        const user = recipient?.user;
        if (user?.id === currentUser?.userId) {
          return senderData;
        }
        return {
          user: {
            userId: user?.userId
          },
          referenceId: null,
          label: user?.email,
          name: user?.name,
          recipientType: 'RECEIVER'
        };
      });

      const isSenderPresent = recipientData?.find(
        (recipient) => recipient?.user?.userId === currentUser?.userId
      );
      if (!isSenderPresent) {
        recipientData?.push(senderData);
      }
      return recipientData;
    }
    return [senderData];
  };

  const getConversationScope = () => {
    if (imConfig?.supportedChannelType === 'DM') {
      return 'APP';
    }
    return isPrivateChannel === true ? 'PRIVATE' : 'PUBLIC';
  };

  const getAdditionalVariables = () => {
    if (ticket?.source === TICKET_SOURCE.Communication) {
      return {
        sendMail: true,
        conversationScope: getConversationScope(),
        recipientDetails: getRecipientDetails()
      };
    }
    return {
      toUsers: [
        {
          email: requester?.email
        }
      ],
      ccUsers: formatUsers(cc),
      bccUsers: formatUsers(bcc),
      hasAdditionalRecipients:
        data?.config?.recipients.hasAdditionalRecipients || false
    };
  };

  const sendConversation = (params) => {
    const status = params?.status;
    const supressNotification = params?.supressNotification;

    let ticketInput;

    let variables = {
      ticketId: data?.id,
      content: template?.content || '',
      attachments: formatAttachments(template?.attachments)
    };

    if (isMSP) {
      const additionalVariables = getAdditionalVariables() || {};
      variables = {
        ...variables,
        ...additionalVariables
      };
      ticketInput = {};
      if (status) {
        ticketInput = {
          status: status
        };
        if (supressNotification) {
          ticketInput = {
            ...ticketInput,
            suppressCloseNotification: supressNotification
          };
        }
      }

      if (isTriageConversation) {
        ticketInput = {
          ...ticketInput,
          handledByAIAgent: {
            featureId: AI_AGENTS_CONSTANT_FEATURE_ID_MAP.TICKET_TRIAGE
          }
        };
      }

      if (ticket?.source !== TICKET_SOURCE.Communication) {
        variables = {
          ...variables,
          fromMailbox: {
            boxId: selectedMailbox?.boxId
          }
        };
      }

      if (!isEmpty(ticketInput)) {
        variables = {
          ...variables,
          ticketInput: ticketInput
        };
      }
    }
    if (!isMSP && ticket?.source === TICKET_SOURCE.Communication) {
      const additionalVariables = getAdditionalVariables() || {};
      variables = {
        ...variables,
        ...additionalVariables
      };
    }

    try {
      setIsLoading(true);
      if (updateTabProperty) {
        updateTabProperty(data?.id, 'onSendingTicketConversation', resetData);
      }
      EventEmitter.dispatch('updateGlobalTicketConversationData', variables);
      saveTicketConversation({
        variables: variables
      });
    } catch (error) {
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const getConnectedDisclosureActions = () => {
    if (isMSP && isStatusAuthorised && splitOptions?.length) {
      const actions = splitOptions.map((status) => {
        const icon = status?.type ? ICON?.[status.type] : 'TicketOutline';
        const label = status?.type ? LABEL_KEYS?.[status.type] : null;

        return {
          content: (
            <div className="flex fx-sb tw-gap-x-2 w100">
              <div
                className="flex actr tw-gap-x-2 w100"
                onClick={() =>
                  sendConversation({
                    status: status?.value
                  })
                }
              >
                <SVGIcon name={icon} />
                {label ? (
                  <FormattedMessage id={label} />
                ) : (
                  <FormattedMessage
                    id="ticket.details.conversation.sendAndStatus"
                    values={{
                      status: status?.value
                    }}
                  />
                )}
              </div>

              {status?.type === 'CLOSE' && (
                <span
                  onClick={() =>
                    sendConversation({
                      status: status?.value,
                      supressNotification: true
                    })
                  }
                  className="fx actr ptr"
                >
                  <Tooltip
                    html={
                      <DisplayText element="p">
                        <FormattedMessage id="ticket.action.closeWithoutNotification" />
                      </DisplayText>
                    }
                    delay={400}
                    duration={250}
                    position="bottom"
                    distance={10}
                    inertia
                    trigger="mouseenter"
                    animation="shift"
                    animateFill={false}
                  >
                    <Badge
                      radial
                      status="attention"
                      icon={
                        <SVGIcon name="NotificationsBellCrossed" size="sm" />
                      }
                    />
                  </Tooltip>
                </span>
              )}
            </div>
          )
        };
      });
      return actions;
    }
    return null;
  };

  const getIsDisabled = () => {
    if (!isMSP) {
      return false;
    }
    if (ticket?.source === TICKET_SOURCE.Communication) {
      if (isPrivateChannel) {
        return isEmpty(conversationTo);
      }
      return false;
    }
    return !selectedMailbox || !ticket?.requester;
  };

  const isDisabled = getIsDisabled();

  const sendConversationWithTicketInput = (data) => {
    console.log(data, 'ash:: data');
  };

  const insertTriageTicketConversation = (data) => {
    const templateData = data?.template;
    setIsTriageConversation(true);
    if (data?.id) {
      EventEmitterWithIndex.dispatch('insertGlobalPopupTemplate', {
        template: templateData,
        id: data?.id,
        type: data?.type
      });
    }
  };

  useEffect(() => {
    getSplitOptions();
    EventEmitter.subscribe(
      'insertTriageTicketConversation',
      insertTriageTicketConversation
    );
    EventEmitter.subscribe(
      'sendConversationWithMandatoryFields',
      sendConversationWithTicketInput
    );

    return function cleanup() {
      EventEmitter.unsubscribe(
        'insertTriageTicketConversation',
        insertTriageTicketConversation
      );
      EventEmitter.unsubscribe('sendConversationWithMandatoryFields');
    };
  }, []);

  const connectionDisclosureData =
    isMSP && isStatusAuthorised
      ? {
          disabled: isDisabled,
          actions: getConnectedDisclosureActions()
        }
      : null;

  return (
    <div>
      <Button
        icon={<SVGIcon name="SendWhite" color="#fff" />}
        primary
        loading={
          loading ||
          ticketStatusConfigLoading ||
          statusLoading ||
          saveConversationLoading
        }
        connectedDisclosure={connectionDisclosureData}
        onClick={() => sendConversation({})}
        disabled={isDisabled}
      >
        <FormattedMessage id="common.send" />
      </Button>
    </div>
  );
};

export default withErrorBoundary(SendTicketConversationAction);
